import cn from "classnames";
import type { Ref } from "react";

import { getFrontendIconPreview } from "@/components/icons/iconRegistry";
import { PhoneIcon } from "@/components/icons/phone.icon";
import { Link } from "@/components/link/link.component";
import { PhoneNumber } from "@/components/phoneNumber/phoneNumber.component";

import { resolveLinkHref } from "@/utils/url.util";

import { brown } from "@/app/font";

import type { FreeQuoteDrawerDefaults } from "./freeQuoteDrawer.defaults";

export const FreeQuoteSuccessDark = ({
  content,
  headingRef,
  firstName,
  installPref,
}: FreeQuoteSuccessDarkProps) => {
  const shopHref = resolveLinkHref(content.shopCta.link);
  const shopOpenInNewTab = Boolean(content.shopCta.link.openInNewTab);
  const greeting = firstName ? `Hi, ${firstName}!` : content.successHeading;

  return (
    <div
      role="status"
      aria-live="polite"
      className="mx-auto flex w-full max-w-4xl flex-col items-center py-4 text-center">
      <h2
        id="free-quote-inline-title"
        ref={headingRef}
        tabIndex={-1}
        className={cn(
          brown.className,
          "text-[36px] leading-tight font-bold text-white outline-none sm:text-[48px]",
        )}>
        {greeting}
      </h2>
      <p
        className={cn(
          brown.className,
          "text-primary mt-2 text-[28px] leading-snug font-bold sm:text-[40px]",
        )}>
        {content.successMessage}
      </p>

      <div className="mt-8 w-full rounded-3xl bg-white/10 px-6 py-8 sm:px-10">
        <p className="text-lg font-semibold text-white">
          {content.nextStepsHeading}
        </p>
        <ul className="mt-8 flex flex-col gap-8 lg:flex-row lg:justify-between lg:gap-6">
          {content.nextSteps.map((step) => {
            const Icon = getFrontendIconPreview(step.icon);
            return (
              <li
                key={step.title}
                className="flex flex-1 flex-col items-center">
                {Icon ? (
                  <Icon
                    width={32}
                    height={32}
                    className="text-primary"
                    aria-hidden="true"
                  />
                ) : null}
                <p
                  className={cn(
                    brown.className,
                    "text-primary mt-3 text-lg font-bold",
                  )}>
                  {step.title}
                </p>
                <p className="mt-1 max-w-[16rem] text-[15px] leading-snug text-white/75">
                  {step.description}
                </p>
              </li>
            );
          })}
        </ul>
      </div>

      <p
        className={cn(
          brown.className,
          "text-primary mt-10 text-[28px] leading-snug font-bold sm:text-[32px]",
        )}>
        {content.skipWaitHeading}
      </p>
      <p className="mt-3 max-w-md text-base leading-snug text-white/80">
        {content.skipWaitMessageCompact}
      </p>
      <div className="mt-6 flex w-full max-w-md flex-col items-stretch gap-3 sm:max-w-none sm:flex-row sm:justify-center">
        <PhoneNumber
          showNumber
          formatNumber
          className={cn(
            brown.className,
            "border-primary text-primary flex w-full items-center justify-center gap-2 rounded-full border-2 p-3 text-[18px] font-bold normal-case! uppercase duration-300 sm:w-auto sm:min-w-[220px]",
          )}
          desktopClassName="lg:flex">
          <PhoneIcon width={20} height={20} aria-hidden="true" title="" />
        </PhoneNumber>
        {installPref === "diy" || installPref === "difm_diy" ? (
          <Link
            href={shopHref}
            primary
            target={shopOpenInNewTab ? "_blank" : undefined}
            rel={shopOpenInNewTab ? "noopener noreferrer" : undefined}
            className="w-full normal-case! sm:w-auto sm:min-w-[200px]">
            {content.shopCta.compactLabel}
          </Link>
        ) : null}
      </div>
    </div>
  );
};

type FreeQuoteSuccessDarkProps = {
  content: FreeQuoteDrawerDefaults;
  headingRef?: Ref<HTMLHeadingElement>;
  firstName?: string;
  installPref?: string;
};
